import { useEffect, useRef, useState } from "react";
import { BarChart3, LoaderCircle } from "lucide-react";
import { apiClient, type MentionQuery } from "@/shared/api/api-client";
import { ApiError, errorMessage } from "@/shared/api/http";
import { ConfirmDialogDetailsBox, ConfirmDialogFrame } from "@/shared/components/ConfirmDialog";
import { recorteToPayload, type ReportRecorte } from "@/features/reports/report-recorte";
import { EmptyRecorteError, resolveReportWindow } from "@/features/reports/report-window";

const RUNNING = new Set(["QUEUED", "STARTED"]);
const POLL_MS = 2500;

type Phase =
  | { kind: "confirm" }
  | { kind: "working" }
  | { kind: "failed"; message: string };

/**
 * "Enviar para o Dashboard" na tela de Menções (2026-10-27, pedido do
 * Alexsander): o dashboard só lê AMOSTRA (salva ou rascunho), então o
 * botão fecha a amostra com o filtro da tela — o mesmo pedido do "Fechar
 * amostra", com a mesma janela do "Enviar para o Versa" — espera o
 * cálculo e abre o dashboard já nela (`?draft=`).
 *
 * Fechar a janela no meio não cancela nada: o cálculo segue no worker e
 * o rascunho aparece em Amostras. O pedido é idempotente pro mesmo
 * filtro (o servidor devolve a execução ativa), então clicar de novo
 * volta a acompanhar a mesma.
 */
export function SendToDashboardButton({
  token,
  buildQuery,
  buildRecorte,
  notInReport,
  disabledReason = null,
  onOpenDraft,
}: {
  token: string;
  buildQuery: () => MentionQuery;
  buildRecorte: () => ReportRecorte;
  notInReport: () => string[];
  /** O mesmo motivo que trava o "Fechar amostra", ou `null`. */
  disabledReason?: string | null;
  /** Quem navega é a rota. */
  onOpenDraft: (draftId: number) => void;
}) {
  const [recorte, setRecorte] = useState<{
    report: ReportRecorte;
    mentions: MentionQuery;
    notInReport: string[];
  } | null>(null);
  return (
    <>
      <button
        type="button"
        className="secondary-button"
        disabled={disabledReason !== null}
        title={disabledReason ?? undefined}
        onClick={() =>
          setRecorte({ report: buildRecorte(), mentions: buildQuery(), notInReport: notInReport() })
        }
      >
        <BarChart3 size={16} />
        Enviar para o Dashboard
      </button>
      {recorte && (
        <SendToDashboardDialog
          token={token}
          recorte={recorte}
          onClose={() => setRecorte(null)}
          onOpenDraft={onOpenDraft}
        />
      )}
    </>
  );
}

function SendToDashboardDialog({
  token,
  recorte,
  onClose,
  onOpenDraft,
}: {
  token: string;
  recorte: { report: ReportRecorte; mentions: MentionQuery; notInReport: string[] };
  onClose: () => void;
  onOpenDraft: (draftId: number) => void;
}) {
  const [phase, setPhase] = useState<Phase>({ kind: "confirm" });
  // Fechar a janela para o acompanhamento (não o cálculo).
  const alive = useRef(true);
  useEffect(() => () => { alive.current = false; }, []);

  const wait = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

  const start = async () => {
    setPhase({ kind: "working" });
    try {
      const span = await resolveReportWindow(token, recorte.report, recorte.mentions);
      let run = await apiClient.requestSocialReport(token, {
        windowStart: span.start,
        windowEnd: span.end,
        recorte: recorteToPayload(recorte.report),
      });
      while (RUNNING.has(run.status)) {
        await wait(POLL_MS);
        if (!alive.current) return;
        try {
          run = await apiClient.socialReportRun(token, run.id);
        } catch (failure) {
          // 429 é pausa, não fim: a execução continua no worker.
          if (failure instanceof ApiError && failure.retryAfterSeconds !== null) {
            await wait(failure.retryAfterSeconds * 1000);
            continue;
          }
          throw failure;
        }
      }
      if (!alive.current) return;
      if (run.report_draft_id) {
        onClose();
        onOpenDraft(run.report_draft_id);
        return;
      }
      setPhase({
        kind: "failed",
        message:
          run.message ??
          (run.status === "FINISHED_EMPTY"
            ? "Nenhuma publicação nesse filtro."
            : "A geração da amostra falhou."),
      });
    } catch (error) {
      if (!alive.current) return;
      setPhase({
        kind: "failed",
        message:
          error instanceof EmptyRecorteError
            ? error.message
            : errorMessage(error, "Não foi possível gerar a amostra."),
      });
    }
  };

  const working = phase.kind === "working";
  return (
    <ConfirmDialogFrame
      title="Enviar para o Dashboard"
      icon={BarChart3}
      onClose={onClose}
      onSubmit={() => void start()}
      actions={
        <>
          <button type="button" className="secondary-button" onClick={onClose}>
            {working ? "Fechar" : "Cancelar"}
          </button>
          <button type="submit" className="primary-button" disabled={working}>
            {working ? (
              <LoaderCircle className="spin" size={16}/>
            ) : phase.kind === "failed" ? (
              "Tentar de novo"
            ) : (
              "Gerar e abrir"
            )}
          </button>
        </>
      }
    >
      {working ? (
        <p role="status">
          Calculando a amostra com o filtro atual. Isso pode levar alguns
          minutos — o dashboard abre sozinho quando terminar. Se fechar
          esta janela, o cálculo continua e o rascunho aparece em Amostras.
        </p>
      ) : (
        <>
          <p>
            O filtro atual vira uma amostra (rascunho, vence em 7 dias) e o
            dashboard abre já nela.
          </p>
          <ConfirmDialogDetailsBox details={{ title: "Fica de fora", items: recorte.notInReport }}/>
        </>
      )}
      {phase.kind === "failed" && (
        <div className="account-feedback error" aria-live="polite">{phase.message}</div>
      )}
    </ConfirmDialogFrame>
  );
}
